/**
 * HustlePlateShell — shared chrome for every `/hustleplate/*` page.
 *
 * Provides:
 *   • Reuse of the platform header + suite footer
 *   • A "Bigmenu" mega-nav that lists every backend product tile so
 *     visitors can jump between Recipe Builder, Bookings, Table
 *     Builder, Terminal, Rewards, etc. without going home first
 *   • A common `<HustlePlateSubPage>` wrapper that centres content
 *     and applies consistent spacing so each ~1000-word sub-page
 *     doesn't reinvent the layout
 */
import { Link, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import {
  ChevronDown, Menu, X, ChefHat, CalendarDays, LayoutPanelTop,
  Monitor, Gift, Bot, Utensils, PackageSearch, Printer, ArrowRight,
  Sparkles, ArrowUpRight, MapPin, Search as SearchIcon,
} from "lucide-react";
import PlatformHeader from "@/components/PlatformHeader";
import SuiteFooter from "@/components/SuiteFooter";
import CalDemoWidget from "@/components/CalDemoWidget";
import FounderChatPill from "@/components/FounderChatPill";

const LOGO =
  "https://customer-assets.emergentagent.com/job_mobile-resto/artifacts/d6gkfgwo_Local%20Hustle%20Logo%20-%20PLAZA%20LIGHT.png";

/* ─── Bigmenu registry ────────────────────────────────────────
 * Add a new backend product here and it lights up in the mega-nav
 * PLUS the mobile drawer everywhere `<HustlePlateBigmenu />` is
 * used.  `href` is the router path; `intro` is the one-liner shown
 * inside the mega-menu popover. */
export const BIGMENU_SECTIONS = [
  {
    heading: "Run the floor",
    items: [
      { key: "terminal",  href: "/terminal",       icon: Monitor,
        label: "Terminal Software",
        intro: "Zero-commission till that runs on any tablet, phone or laptop." },
      { key: "bookings",  href: "/bookings",       icon: CalendarDays,
        label: "Bookings",
        intro: "Live table availability, deposits, no-show fees — no OpenTable rent." },
      { key: "tables",    href: "/table-builder",  icon: LayoutPanelTop,
        label: "Table Builder",
        intro: "Draw your floor once and every screen — POS, web, hostess — updates." },
      { key: "kitchen",   href: "/kitchen-display", icon: Printer,
        label: "Kitchen Display",
        intro: "KDS screen for chefs, auto-routing tickets by course + station." },
    ],
  },
  {
    heading: "Grow the menu",
    items: [
      { key: "recipe",    href: "/recipe-builder", icon: ChefHat,
        label: "Recipe Builder",
        intro: "Costed recipes, sub-recipes, allergens, plate margin in seconds." },
      { key: "menu",      href: "/menu-builder",   icon: Utensils,
        label: "Menu Builder",
        intro: "Design digital + printable menus from the same recipe library." },
      { key: "inventory", href: "/inventory",      icon: PackageSearch,
        label: "Inventory",
        intro: "Auto-deducting stock, low-stock alerts, supplier re-order flow." },
    ],
  },
  {
    heading: "Keep the guest",
    items: [
      { key: "rewards",       href: "/rewards",         icon: Gift,
        label: "Rewards System",
        intro: "In-house loyalty, tiers, birthday credits, gift vouchers." },
      { key: "receptionist",  href: "/ai-receptionist", icon: Bot,
        label: "AI Receptionist",
        intro: "Answers every call in 12 languages, takes bookings, sends SMS." },
    ],
  },
  {
    heading: "Grow your reach",
    items: [
      { key: "directory",     href: "/directory-sales", icon: MapPin,
        label: "HustleDirectory",
        intro: "Claim your ABN-verified public listing — free forever, Featured when you're ready." },
      { key: "directory-browse", href: "/directory",    icon: SearchIcon,
        label: "Browse the Directory",
        intro: "23,921 Aussie venues, 9,274 suburbs — see how customers find you today." },
    ],
  },
];

/* ─── Bigmenu — desktop mega-nav + mobile drawer ─────────────── */
export function HustlePlateBigmenu({ testid = "hp-bigmenu" }) {
  const [open, setOpen] = useState(false);          // desktop dropdown key
  const [mobileOpen, setMobileOpen] = useState(false);
  const wrapRef = useRef(null);
  const loc = useLocation();

  useEffect(() => { setOpen(false); setMobileOpen(false); }, [loc.pathname]);
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener("mousedown", onDoc);
    return () => window.removeEventListener("mousedown", onDoc);
  }, []);

  return (
    <nav ref={wrapRef}
         className="sticky top-0 z-40 border-b border-emerald-200/40
                    dark:border-slate-800 bg-white/90 dark:bg-slate-950/90
                    backdrop-blur"
         data-testid={testid}>
      <div className="max-w-7xl mx-auto px-4 h-14 flex items-center gap-3">
        <Link to="/hustleplate" className="flex items-center gap-2 shrink-0"
              data-testid="hp-bigmenu-brand">
          <img src={LOGO} alt="HustlePlate" className="h-7" />
          <span className="hidden sm:inline text-sm font-semibold text-emerald-800
                              dark:text-emerald-300">
            HustlePlate
          </span>
        </Link>

        {/* Desktop mega-nav */}
        <div className="hidden lg:flex ml-6 gap-1">
          {BIGMENU_SECTIONS.map((sec) => (
            <div key={sec.heading} className="relative">
              <button
                data-testid={`hp-bigmenu-btn-${sec.heading.toLowerCase().replace(/\s/g, "-")}`}
                onClick={() => setOpen(open === sec.heading ? null : sec.heading)}
                className="flex items-center gap-1 h-14 px-4 text-sm font-medium
                              text-slate-700 dark:text-slate-200
                              hover:text-emerald-700 dark:hover:text-emerald-300
                              transition-colors">
                {sec.heading}
                <ChevronDown className={`h-4 w-4 transition-transform
                                             ${open === sec.heading ? "rotate-180" : ""}`} />
              </button>
              {open === sec.heading && (
                <div className="absolute top-full left-0 mt-1 w-[420px] max-w-[92vw]
                                   bg-white dark:bg-slate-900 rounded-xl shadow-2xl
                                   ring-1 ring-slate-200 dark:ring-slate-800 p-3"
                     data-testid={`hp-bigmenu-popover-${sec.heading.toLowerCase().replace(/\s/g, "-")}`}>
                  {sec.items.map((it) => (
                    <Link key={it.key} to={it.href}
                          data-testid={`hp-bigmenu-link-${it.key}`}
                          className="flex items-start gap-3 p-3 rounded-lg
                                        hover:bg-emerald-50 dark:hover:bg-slate-800
                                        transition-colors group">
                      <div className="h-9 w-9 rounded-lg bg-emerald-100
                                          dark:bg-emerald-900/30 text-emerald-700
                                          dark:text-emerald-300 flex items-center
                                          justify-center shrink-0">
                        <it.icon className="h-5 w-5" strokeWidth={1.5} />
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-slate-900
                                            dark:text-slate-100 flex items-center gap-1">
                          {it.label}
                          <ArrowUpRight className="h-3 w-3 opacity-0
                                                     group-hover:opacity-100 transition" />
                        </div>
                        <div className="text-xs text-slate-600 dark:text-slate-400
                                            mt-0.5">
                          {it.intro}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="ml-auto flex items-center gap-2">
          <Link to="/login" data-testid="hp-bigmenu-signin"
                className="hidden sm:inline text-sm font-medium text-slate-700
                              dark:text-slate-300 hover:text-emerald-700
                              dark:hover:text-emerald-300">
            Sign in
          </Link>
          <Link to="/signup" data-testid="hp-bigmenu-cta"
                className="hidden sm:inline-flex items-center gap-1 bg-emerald-600
                              hover:bg-emerald-500 text-white px-4 py-2 rounded-full
                              text-sm font-semibold transition-colors">
            Start free trial
            <ArrowRight className="h-4 w-4" />
          </Link>
          <button className="lg:hidden text-slate-700 dark:text-slate-200 p-2"
                  onClick={() => setMobileOpen((v) => !v)}
                  data-testid="hp-bigmenu-mobile-toggle">
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-slate-200 dark:border-slate-800
                        bg-white dark:bg-slate-950 max-h-[70vh] overflow-y-auto"
             data-testid="hp-bigmenu-drawer">
          {BIGMENU_SECTIONS.map((sec) => (
            <div key={sec.heading} className="border-b border-slate-100
                                                dark:border-slate-800 last:border-0">
              <div className="px-4 pt-3 pb-1 text-[11px] uppercase tracking-widest
                                text-emerald-700 dark:text-emerald-300 font-semibold">
                {sec.heading}
              </div>
              {sec.items.map((it) => (
                <Link key={it.key} to={it.href}
                      data-testid={`hp-bigmenu-mobile-${it.key}`}
                      className="flex items-start gap-3 px-4 py-3 hover:bg-emerald-50
                                    dark:hover:bg-slate-900 transition-colors">
                  <it.icon className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />
                  <div>
                    <div className="text-sm font-semibold text-slate-900
                                        dark:text-slate-100">
                      {it.label}
                    </div>
                    <div className="text-xs text-slate-600 dark:text-slate-400 mt-0.5">
                      {it.intro}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ))}
        </div>
      )}
    </nav>
  );
}

/* ─── Shared wrapper for sub-pages ───────────────────────────── */
export default function HustlePlateSubPage({ children, testid = "hp-subpage" }) {
  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 text-slate-900
                    dark:text-slate-100"
         data-testid={testid}>
      <PlatformHeader logo={LOGO} />
      <HustlePlateBigmenu />
      <main>
        {children}
        {/* iter260: every sub-page ends with a "Book a demo" band so
          * warm readers can pick a slot before they bounce back to the
          * main funnel. Slug is env-driven — see CalDemoWidget.jsx. */}
        <CalDemoWidget testid="hp-subpage-cal" />
      </main>
      {/* iter261: floating chat-with-founder pill (Telegram + email) so
        * restaurant owners can DM during service hours without booking
        * a formal demo. Handles env-driven — see FounderChatPill.jsx. */}
      <FounderChatPill testid="hp-subpage-founder-chat" />
      <SuiteFooter />
    </div>
  );
}

/* Convenient hero block reusable across all sub-pages */
export function SubPageHero({ eyebrow, title, subtitle, heroImage, primaryCta,
                              secondaryCta, testid = "hp-sub-hero" }) {
  return (
    <section className="relative isolate overflow-hidden bg-gradient-to-br
                        from-emerald-50 via-white to-amber-50
                        dark:from-slate-900 dark:via-slate-950 dark:to-emerald-950/40"
             data-testid={testid}>
      <div className="max-w-7xl mx-auto px-6 py-16 md:py-24 grid md:grid-cols-2
                      gap-12 items-center">
        <div>
          <p className="text-xs md:text-sm font-semibold uppercase tracking-widest
                            text-emerald-700 dark:text-emerald-400">
            {eyebrow}
          </p>
          <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-black leading-tight
                            text-slate-900 dark:text-white">
            {title}
          </h1>
          <p className="mt-5 text-base md:text-lg text-slate-700 dark:text-slate-300
                            leading-relaxed max-w-xl">
            {subtitle}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            {primaryCta && (
              <Link to={primaryCta.href}
                    data-testid={`${testid}-cta-primary`}
                    className="inline-flex items-center gap-2 bg-emerald-600
                                  hover:bg-emerald-500 text-white font-semibold
                                  px-6 py-3 rounded-full shadow-lg
                                  hover:shadow-xl transition">
                {primaryCta.label}
                <ArrowRight className="h-4 w-4" />
              </Link>
            )}
            {secondaryCta && (
              <Link to={secondaryCta.href}
                    data-testid={`${testid}-cta-secondary`}
                    className="inline-flex items-center gap-2 border-2
                                  border-emerald-600 text-emerald-700
                                  dark:text-emerald-300 hover:bg-emerald-50
                                  dark:hover:bg-emerald-950/40 font-semibold
                                  px-6 py-3 rounded-full transition">
                {secondaryCta.label}
              </Link>
            )}
          </div>
        </div>
        <div className="relative">
          <img src={heroImage} alt="" loading="eager"
               className="rounded-2xl shadow-2xl aspect-[4/3] object-cover w-full" />
          <div className="absolute -bottom-4 -right-4 bg-emerald-500 text-white
                            px-4 py-2 rounded-full shadow-lg text-xs font-bold
                            flex items-center gap-1">
            <Sparkles className="h-3.5 w-3.5" />
            Included with HustlePlate
          </div>
        </div>
      </div>
    </section>
  );
}

/* Tiny helper block used across sub-pages */
export function BackToPlate({ testid = "hp-back-plate" }) {
  return (
    <div className="max-w-7xl mx-auto px-6 pb-12 pt-4">
      <Link to="/hustleplate"
            data-testid={testid}
            className="text-sm font-semibold text-emerald-700 dark:text-emerald-300
                          hover:underline inline-flex items-center gap-1">
        ← Back to HustlePlate overview
      </Link>
    </div>
  );
}
